import { ChartDataPoint } from 'features/systemLens'

/**
 * A sensor's observed data range. `min`/`max` are absent (undefined) when the
 * sensor has no finite data points in the current window — never an
 * Infinity/-Infinity sentinel, so consumers can render "no data" states
 * without leaking `Infinity` into the UI.
 */
export interface SensorRangeData {
  min?: number
  max?: number
  unit: string
}

export type SensorRangeMap = Record<string, SensorRangeData>

export type CustomAxisRangeMap = Record<string, { min?: number; max?: number }>

interface AxisRangeInput {
  min?: number
  max?: number
}

interface AxisRangeOverride {
  min?: number
  max?: number
}

export interface AxisRange {
  min: number
  max: number
}

const DEFAULT_AXIS_RANGE: AxisRange = { min: 0, max: 1 }

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/** True when a sensor range has settled on real, finite data. */
export const hasFiniteRange = (
  range: AxisRangeInput | undefined
): range is { min: number; max: number } =>
  !!range && isFiniteNumber(range.min) && isFiniteNumber(range.max)

/**
 * Pads [min, max] by 10% for breathing room around the plotted series.
 *
 * A flat series (min === max — a status tag, a binary tag, or any tag zoomed
 * into a steady-state stretch) would otherwise get a zero buffer and collapse
 * to [v, v]. uPlot receives that as an explicit `{ auto: false, range: [v, v] }`
 * and normalizes every point as (val - min) / (max - min) = 0 / 0 = NaN, so the
 * series has no pixel positions and the chart renders blank. uPlot's own
 * min === max rescue only covers its auto-range branch, which an explicit range
 * never reaches, so the padding has to happen here.
 *
 * For the flat case we fall back to 10% of the value's magnitude (keeping the
 * padding proportional the way it is for non-flat series, so 0.5 stays readable
 * as [0.45, 0.55] rather than being swamped) with an absolute floor of 1 for
 * v === 0, where a relative pad is still zero.
 */
export const applyAxisBuffer = (min: number, max: number): AxisRange => {
  const buffer = (max - min) * 0.1 || Math.abs(max) * 0.1 || 1
  return {
    min: min - buffer,
    max: max + buffer,
  }
}

const getAutoYAxisRange = (
  dataRange: AxisRangeInput | undefined
): AxisRange => {
  if (!hasFiniteRange(dataRange)) {
    return { ...DEFAULT_AXIS_RANGE }
  }

  return applyAxisBuffer(dataRange.min, dataRange.max)
}

export const resolveYAxisRange = (
  dataRange: AxisRangeInput | undefined,
  customRange?: AxisRangeOverride
): AxisRange => {
  const autoRange = getAutoYAxisRange(dataRange)
  const resolved = {
    min: customRange?.min ?? autoRange.min,
    max: customRange?.max ?? autoRange.max,
  }
  // Safety net: never hand the plot an inverted or collapsed range. Custom
  // bounds are guarded at commit/transition time, but pre-existing bad data
  // (or a partial custom bound combined with an auto bound it crosses) can
  // still produce min >= max here — uPlot would then normalize points to NaN
  // and render blank. Fall back to the auto range instead.
  if (resolved.min >= resolved.max) {
    return autoRange
  }
  return resolved
}

export const resolveSingleScaleYAxisRange = (
  allItems: Array<{ id: string }>,
  sensorRanges: Record<string, AxisRangeInput>,
  customRange?: AxisRangeOverride
): AxisRange => {
  let overallMin = Infinity
  let overallMax = -Infinity

  allItems.forEach(({ id: itemId }) => {
    const data = sensorRanges[itemId]
    if (hasFiniteRange(data)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  return resolveYAxisRange(
    Number.isFinite(overallMin) && Number.isFinite(overallMax)
      ? { min: overallMin, max: overallMax }
      : undefined,
    customRange
  )
}

/**
 * The shared data range all pens see in single-scale mode, or null while no
 * pen has finite data. Unlike `resolveSingleScaleYAxisRange` this never
 * fabricates a default range — callers use null to show "no data".
 */
export const getSharedAutoRange = (
  allItems: Array<{ id: string }>,
  sensorRanges: SensorRangeMap
): AxisRange | null => {
  let overallMin = Infinity
  let overallMax = -Infinity

  allItems.forEach(({ id: itemId }) => {
    const data = sensorRanges[itemId]
    if (hasFiniteRange(data)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  return Number.isFinite(overallMin) && Number.isFinite(overallMax)
    ? { min: overallMin, max: overallMax }
    : null
}

export const computeSingleScaleRanges = (
  allItems: Array<{ id: string; name?: string }>,
  sensorRanges: SensorRangeMap,
  currentCustomRanges?: CustomAxisRangeMap
): CustomAxisRangeMap => {
  // Build a combined range object that uses custom ranges if available, otherwise auto ranges
  const effectiveRanges: SensorRangeMap = {}
  allItems.forEach(({ id: itemId }) => {
    const customRange = currentCustomRanges?.[itemId]
    const autoRange = sensorRanges[itemId]

    if (
      customRange &&
      customRange.min !== undefined &&
      customRange.max !== undefined
    ) {
      effectiveRanges[itemId] = {
        min: customRange.min,
        max: customRange.max,
        unit: autoRange?.unit || '',
      }
    } else if (autoRange) {
      effectiveRanges[itemId] = autoRange
    }
  })

  // For single scale, just find the overall min/max from the effective ranges
  let overallMin = Infinity
  let overallMax = -Infinity

  allItems.forEach((item) => {
    const data = effectiveRanges[item.id]
    if (hasFiniteRange(data)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  if (isFinite(overallMin) && isFinite(overallMax)) {
    const newRanges: CustomAxisRangeMap = {}
    const formattedMin = Number(overallMin.toPrecision(4))
    const formattedMax = Number(overallMax.toPrecision(4))
    allItems.forEach(({ id: itemId }) => {
      newRanges[itemId] = { min: formattedMin, max: formattedMax }
    })
    newRanges['single-scale'] = { min: formattedMin, max: formattedMax }
    return newRanges
  }
  return {}
}

export const computeMultipleScaleRanges = (
  allItems: Array<{ id: string; name?: string }>,
  chartData: ChartDataPoint[],
  sensorRanges?: SensorRangeMap
): CustomAxisRangeMap => {
  const newRanges: CustomAxisRangeMap = {}

  // If sensorRanges are provided (which already have buffer), use them
  if (sensorRanges) {
    allItems.forEach(({ id: itemId }) => {
      const range = sensorRanges[itemId]
      if (hasFiniteRange(range)) {
        newRanges[itemId] = {
          min: Number(range.min.toPrecision(4)),
          max: Number(range.max.toPrecision(4)),
        }
      }
    })
  } else {
    // Otherwise recompute from chart data with buffer
    allItems.forEach(({ id: itemId }) => {
      let min = Infinity
      let max = -Infinity

      chartData.forEach((point) => {
        const value = point[itemId]
        if (typeof value === 'number' && !isNaN(value)) {
          min = Math.min(min, value)
          max = Math.max(max, value)
        }
      })

      if (isFinite(min) && isFinite(max)) {
        const buffered = applyAxisBuffer(min, max)
        newRanges[itemId] = {
          min: Number(buffered.min.toPrecision(4)),
          max: Number(buffered.max.toPrecision(4)),
        }
      }
    })
  }

  return newRanges
}

export function calculateOverallRangeWithBuffer(
  items: Array<{ id: string; name?: string }>,
  sensorRanges: SensorRangeMap
): { bufferedMin: number; bufferedMax: number } | null {
  if (items.length === 0) {
    return null
  }

  let overallMin = Infinity
  let overallMax = -Infinity
  items.forEach((item) => {
    const data = sensorRanges[item.id]
    if (hasFiniteRange(data)) {
      overallMin = Math.min(overallMin, data.min)
      overallMax = Math.max(overallMax, data.max)
    }
  })

  if (isFinite(overallMin) && isFinite(overallMax)) {
    const buffered = applyAxisBuffer(overallMin, overallMax)
    return {
      bufferedMin: buffered.min,
      bufferedMax: buffered.max,
    }
  }

  return null
}

/** True when any pen (or the shared single-scale entry) has a custom bound. */
export const hasAnyCustomBound = (ranges: CustomAxisRangeMap): boolean =>
  Object.values(ranges).some(
    (range) => range && (range.min !== undefined || range.max !== undefined)
  )

/**
 * Drops entries that no current pen owns. Stale entries are not cosmetic:
 * `hasAnyCustomBound` keeps auto-scale OFF as long as ANY entry carries a
 * bound, so a removed pen's leftover range wedges the Auto state with nothing
 * visible left to clear — and the wedge then persists through local state.
 * The shared `single-scale` entry only means something in single-scale mode,
 * so it is likewise dropped when `keepSingleScale` is false.
 *
 * Returns `ranges` unchanged (same identity) when nothing needs pruning, so
 * callers can use identity to skip a state update.
 */
export const pruneCustomAxisRanges = (
  ranges: CustomAxisRangeMap,
  currentItemIds: ReadonlySet<string>,
  keepSingleScale: boolean
): CustomAxisRangeMap => {
  const staleKeys = Object.keys(ranges).filter((key) =>
    key === 'single-scale' ? !keepSingleScale : !currentItemIds.has(key)
  )
  if (staleKeys.length === 0) {
    return ranges
  }
  const next = { ...ranges }
  staleKeys.forEach((key) => delete next[key])
  return next
}

/**
 * Applies a committed min/max edit to the custom-range map.
 *
 * `linkedItemIds` is non-null in single-scale mode: the edit fans out to every
 * pen plus the shared `single-scale` entry, so all pens stay on one linked
 * scale. In multi mode only the edited pen changes. Clearing a bound (value
 * undefined) reverts it to auto; entries with no bounds left are pruned.
 *
 * An edit that would INVERT a range (min > max) is rejected: the chart schema
 * refines `max >= min`, so an inverted pair would poison every subsequent
 * save/local-state parse. Rejection returns `prevRanges` unchanged (same
 * identity), which callers treat as revert-to-previous.
 *
 * The inversion check runs against the EFFECTIVE counterpart bound: the
 * custom counterpart when one is set, otherwise the finite auto bound from
 * `autoRanges` (what the axis actually renders when only one bound is
 * custom — a partial custom min is combined with the AUTO max at resolve
 * time, so a min above the auto max would render inverted immediately).
 * The check is all-or-nothing across every target: one offending pen rejects
 * the whole edit before any mutation.
 */
export const applyAxisBoundEdit = (
  prevRanges: CustomAxisRangeMap,
  linkedItemIds: string[] | null,
  sensorId: string,
  type: 'min' | 'max',
  value: number | undefined,
  autoRanges?: Record<string, AxisRangeInput>
): CustomAxisRangeMap => {
  const next: CustomAxisRangeMap = { ...prevRanges }

  const targetIds = linkedItemIds
    ? [...linkedItemIds, 'single-scale']
    : [sensorId]

  if (value !== undefined) {
    const wouldInvert = targetIds.some((id) => {
      const customCounterpart =
        type === 'min' ? prevRanges[id]?.max : prevRanges[id]?.min
      const autoCounterpart =
        type === 'min' ? autoRanges?.[id]?.max : autoRanges?.[id]?.min
      const counterpart =
        customCounterpart !== undefined
          ? customCounterpart
          : isFiniteNumber(autoCounterpart)
            ? autoCounterpart
            : undefined
      if (counterpart === undefined) {
        return false
      }
      return type === 'min' ? value > counterpart : value < counterpart
    })
    if (wouldInvert) {
      return prevRanges
    }
  }

  targetIds.forEach((id) => {
    const updated = { ...prevRanges[id], [type]: value }
    if (updated.min === undefined && updated.max === undefined) {
      delete next[id]
    } else {
      next[id] = updated
    }
  })

  return next
}

/**
 * The custom ranges to adopt when the scale-mode toggle flips.
 *
 * To multi (`newSingleScaleMode` false): per-pen values are preserved exactly —
 * every pen keeps whatever it had (in single mode that is the shared value) and
 * simply becomes individually editable. The shared `single-scale` entry is
 * dropped: it has no owning surface in multi mode, so leaving it behind would
 * keep `hasAnyCustomBound` true (auto-scale wedged off) even after the user
 * clears every visible pen.
 *
 * To single: the last value the user committed in the min column applies to
 * all mins, and likewise for max (`lastModified`, tracked per column since
 * mount). A column with no modification history falls back to the current
 * shared/computed range. With no history and no custom ranges at all, pens
 * stay on auto scale.
 */
export const computeScaleModeTransitionRanges = (
  newSingleScaleMode: boolean,
  allItems: Array<{ id: string; name?: string }>,
  sensorRanges: SensorRangeMap,
  prevCustomRanges: CustomAxisRangeMap,
  lastModified: { min?: number; max?: number }
): CustomAxisRangeMap => {
  if (!newSingleScaleMode) {
    if ('single-scale' in prevCustomRanges) {
      const next = { ...prevCustomRanges }
      delete next['single-scale']
      return next
    }
    return prevCustomRanges
  }

  const hasHistory =
    lastModified.min !== undefined || lastModified.max !== undefined

  if (!hasHistory) {
    if (!hasAnyCustomBound(prevCustomRanges)) {
      // Nothing customized and nothing remembered — stay on auto.
      return {}
    }
    return computeSingleScaleRanges(allItems, sensorRanges, prevCustomRanges)
  }

  const fallback = computeSingleScaleRanges(
    allItems,
    sensorRanges,
    prevCustomRanges
  )['single-scale']

  let min = lastModified.min ?? fallback?.min
  let max = lastModified.max ?? fallback?.max

  // Never emit an inverted pair: combining a remembered bound with a
  // fallback-sourced one (or two remembered bounds typed on different pens)
  // can cross. An inverted pair here would fan out to EVERY pen plus
  // 'single-scale', get persisted by local state, then be silently deleted by
  // `sanitizeInvertedYAxisRanges` on the next hydration — the user's bounds
  // vanish and a save can never converge with the sanitized resolve.
  if (min !== undefined && max !== undefined && min > max) {
    const minFromHistory = lastModified.min !== undefined
    const maxFromHistory = lastModified.max !== undefined
    if (minFromHistory && maxFromHistory) {
      // The user's own remembered pair is self-contradictory (typed on
      // different pens): discard the history and use the fallback pair.
      min = fallback?.min
      max = fallback?.max
      if (min !== undefined && max !== undefined && min > max) {
        // No valid pair remains — stay on auto.
        return {}
      }
    } else if (minFromHistory) {
      // Keep the user's bound; drop the conflicting fallback-sourced one so
      // it resolves to auto.
      max = undefined
    } else {
      min = undefined
    }
  }

  if (min === undefined && max === undefined) {
    return {}
  }

  const shared = {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  }

  const newRanges: CustomAxisRangeMap = {}
  allItems.forEach(({ id: itemId }) => {
    newRanges[itemId] = { ...shared }
  })
  newRanges['single-scale'] = { ...shared }
  return newRanges
}
